import React, { useEffect, useRef, useState } from "react";
import { useDialogFocus } from "../../hook/useFormKeyboard";
import { useDebounceEffect } from "../../hook/useDebounceEffect";
import { getListProductOrderTemplate } from "../../services/inventoryCorrections.service";
import { getGroupProduct } from "../../services/items.services";
import { showApiError } from "../../services/apiError";
import { BORDER_SOFT, Icon, MONO, TEAL, TEXT, TEXT_DIM, TEXT_MUTED } from "../../components/list";
import { defaultUnitId, unitsOf } from "./orderTemplateLogic";
import { Chip, DialogFrame, HEAD_BG, INK, PillButton, VISUALLY_HIDDEN, fieldStyle, fill, tr } from "./orderTemplateUi";

const PAGE_SIZE = 20;

export interface ProductPick {
  product: any;
  unitId: string;
}

interface AddProductsDialogProps {
  phone: boolean;
  language: any;
  templateName: string;
  // Units each product already has on the template, saved or not.
  inTemplate: Map<string, Set<string>>;
  onClose: () => void;
  onAdd: (picks: ProductPick[]) => void;
}

const ellipsis: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/** Any company product, searched on the server; mounted only while open. */
const AddProductsDialog: React.FC<AddProductsDialogProps> = ({ phone, language, templateName, inTemplate, onClose, onAdd }) => {
  const t = (key: string) => tr(language, key);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(true, dialogRef, onClose);

  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [groupId, setGroupId] = useState("");
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState<Map<string, any>>(() => new Map());
  const [unitChoice, setUnitChoice] = useState<Record<string, string>>({});
  const requestSeq = useRef(0);

  useEffect(() => {
    const before = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = before;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    getGroupProduct(localStorage.getItem("companyId"), 1, 1000, "")
      .then((response: any) => {
        const rows: any[] = response?.data?.productGroupWithCompany ?? response?.data?.data?.productGroupWithCompany ?? [];
        if (alive) setGroups(rows.filter((row) => row?.id).map((row) => ({ id: String(row.id), name: String(row.groupName ?? "") })));
      })
      // A missing group list only costs the Group filter its options.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  useDebounceEffect(() => setTerm(search.trim()), [search], 300);

  const load = async (nextPage: number, append: boolean) => {
    const mine = ++requestSeq.current;
    setLoading(true);
    setFailed(false);
    try {
      const response: any = await getListProductOrderTemplate(nextPage, PAGE_SIZE, term, "", groupId || undefined);
      if (mine !== requestSeq.current) return;
      const rows: any[] = Array.isArray(response?.data?.productWithCompany) ? response.data.productWithCompany : [];
      setItems((prev) => {
        if (!append) return rows;
        const seen = new Set(prev.map((row) => row.id));
        return [...prev, ...rows.filter((row) => !seen.has(row.id))];
      });
      setTotal(Number(response?.data?.count) || 0);
      setPage(nextPage);
    } catch (error) {
      if (mine !== requestSeq.current) return;
      setFailed(true);
      showApiError(error, { language });
    } finally {
      if (mine === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, groupId]);

  const takenUnits = (product: any) => inTemplate.get(String(product.id)) ?? new Set<string>();
  // Full = every unit is on the template already (a product without units counts once).
  const isFull = (product: any) => {
    const taken = takenUnits(product);
    const units = unitsOf(product);
    return taken.size > 0 && (units.length ? units.every((unit) => taken.has(unit.id)) : true);
  };
  const takenLabel = (product: any) => {
    const taken = takenUnits(product);
    const names = unitsOf(product).filter((unit) => taken.has(unit.id)).map((unit) => unit.name).filter(Boolean);
    return names.length ? fill(t("In template: {units}"), { units: names.join(", ") }) : t("In template");
  };
  // A product already on the template starts on its first unit that is not.
  const defaultUnitFor = (product: any) => {
    const taken = takenUnits(product);
    const units = unitsOf(product);
    if (!taken.size) return defaultUnitId(units);
    const free = units.filter((unit) => !taken.has(unit.id));
    return free.find((unit) => unit.isBase)?.id ?? free[0]?.id ?? defaultUnitId(units);
  };
  const unitFor = (product: any) => unitChoice[product.id] ?? defaultUnitFor(product);

  const toggle = (product: any) => {
    const id = String(product.id);
    if (isFull(product)) return;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, product);
      return next;
    });
  };

  const count = selected.size;
  const add = () => onAdd(Array.from(selected.values()).map((product) => ({ product, unitId: unitFor(product) })));
  const title = templateName.trim() ? fill(t("Add products to {name}"), { name: templateName.trim() }) : t("Add products");

  const unitSelect = (product: any, labelledBy: string, disabled: boolean) => {
    const units = unitsOf(product);
    const taken = takenUnits(product);
    if (!units.length) return <span style={{ color: TEXT_DIM, fontSize: "13px" }}>—</span>;
    return (
      <select
        className="ot-field"
        value={unitFor(product)}
        disabled={disabled}
        aria-labelledby={labelledBy}
        onChange={(e) => setUnitChoice((prev) => ({ ...prev, [product.id]: e.target.value }))}
        style={{ ...fieldStyle(phone), height: phone ? "44px" : "34px", borderRadius: "8px", padding: "0 6px", fontSize: phone ? "16px" : "13.5px" }}
      >
        {!unitFor(product) && <option value="">{t("Select")}</option>}
        {units.map((unit) => (
          <option key={unit.id} value={unit.id} disabled={!disabled && taken.has(unit.id)}>
            {unit.name}
          </option>
        ))}
      </select>
    );
  };

  const checkbox = (product: any, checked: boolean, already: boolean, label?: string) => (
    <input
      type="checkbox"
      className="ot-check"
      checked={checked}
      disabled={already}
      aria-label={label}
      onChange={() => toggle(product)}
      style={{ width: "18px", height: "18px", margin: 0, accentColor: TEAL, cursor: already ? "default" : "pointer", flex: "none" }}
    />
  );

  const th: React.CSSProperties = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    height: "38px",
    padding: "0 8px",
    backgroundColor: HEAD_BG,
    borderBottom: `1px solid ${BORDER_SOFT}`,
    textAlign: "left",
    fontSize: "12.5px",
    fontWeight: 600,
    color: TEXT_DIM,
    whiteSpace: "nowrap",
  };
  const td: React.CSSProperties = { height: "46px", padding: "0 8px", borderBottom: `1px solid ${BORDER_SOFT}`, verticalAlign: "middle" };

  const desktopList = (
    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "44px" }} />
        <col />
        <col style={{ width: "112px" }} />
        <col style={{ width: "112px" }} />
        <col style={{ width: "120px" }} />
      </colgroup>
      <thead>
        <tr>
          <th scope="col" style={{ ...th, paddingLeft: "16px" }}>
            <span style={VISUALLY_HIDDEN}>{t("Select")}</span>
          </th>
          <th scope="col" style={th}>{t("Product")}</th>
          <th scope="col" style={th}>{t("SKU")}</th>
          <th scope="col" style={th}>{t("Group")}</th>
          <th scope="col" id="ot-pick-col-unit" style={{ ...th, paddingRight: "16px" }}>
            {t("Order unit")}
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((product) => {
          const id = String(product.id);
          const already = isFull(product);
          const taken = takenUnits(product).size > 0;
          const name = String(product.productName ?? "");
          return (
            <tr
              key={id}
              className="ot-pick-row"
              data-selected={selected.has(id)}
              data-disabled={already}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("input, select, label")) return;
                toggle(product);
              }}
            >
              <td style={{ ...td, paddingLeft: "16px" }}>
                {checkbox(product, already || selected.has(id), already, taken ? `${name} (${takenLabel(product)})` : name)}
              </td>
              <td style={td}>
                {/* The chip sits under the name, so a list of units never squeezes the name. */}
                <div style={{ display: "flex", flexDirection: taken ? "column" : "row", alignItems: taken ? "flex-start" : "center", gap: taken ? "2px" : "8px", minWidth: 0 }}>
                  <span id={`ot-pick-name-${id}`} title={name} style={{ ...ellipsis, maxWidth: "100%", fontSize: "14px", fontWeight: 500, color: already ? TEXT_MUTED : TEXT }}>
                    {name}
                  </span>
                  {taken && <Chip tone="grey" truncate>{takenLabel(product)}</Chip>}
                </div>
              </td>
              <td style={{ ...td, ...ellipsis, fontFamily: MONO, fontSize: "12px", color: TEXT_MUTED }} title={product.sku}>
                {product.sku}
              </td>
              <td style={{ ...td, ...ellipsis, fontSize: "13px", color: TEXT_MUTED }} title={product.productGroup?.groupName}>
                {product.productGroup?.groupName ?? ""}
              </td>
              <td style={{ ...td, paddingRight: "16px" }}>{unitSelect(product, `ot-pick-col-unit ot-pick-name-${id}`, already)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const phoneList = (
    <>
      <span id="ot-pick-unit-label" style={VISUALLY_HIDDEN}>
        {t("Order unit")}
      </span>
      <ul style={{ listStyle: "none", margin: 0, padding: "0 16px" }}>
        {items.map((product) => {
          const id = String(product.id);
          const already = isFull(product);
          const taken = takenUnits(product).size > 0;
          const name = String(product.productName ?? "");
          const meta = [product.sku, product.productGroup?.groupName].filter(Boolean).join(" · ");
          return (
            <li key={id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0", borderBottom: `1px solid ${BORDER_SOFT}` }}>
              <label style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0, minHeight: "44px", cursor: already ? "default" : "pointer" }}>
                {checkbox(product, already || selected.has(id), already)}
                <span style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                  <span id={`ot-pick-name-${id}`} style={{ ...ellipsis, fontSize: "15px", fontWeight: 500, color: already ? TEXT_MUTED : TEXT }}>
                    {name}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                    {meta && <span style={{ ...ellipsis, fontFamily: MONO, fontSize: "12px", color: TEXT_DIM }}>{meta}</span>}
                    {taken && <Chip tone="grey" truncate>{takenLabel(product)}</Chip>}
                  </span>
                </span>
              </label>
              <div style={{ width: "112px", flex: "none" }}>{unitSelect(product, `ot-pick-unit-label ot-pick-name-${id}`, already)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );

  const status = (() => {
    if (loading && !items.length) return t("Loading...");
    if (failed && !items.length) return t("Products could not be loaded.");
    if (!items.length) return t("No products found");
    return fill(t("Showing {n} of {m} products"), { n: items.length, m: Math.max(total, items.length) });
  })();

  return (
    <DialogFrame phone={phone} onClose={onClose} dialogRef={dialogRef} labelledBy="ot-add-title" width={600} fixedHeight>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: phone ? "10px 16px 12px" : "18px 16px 12px 24px", flex: "none" }}>
        <h2 id="ot-add-title" style={{ flex: 1, minWidth: 0, margin: 0, fontSize: "18px", lineHeight: "26px", fontWeight: 600, color: INK, overflowWrap: "anywhere" }}>
          {title}
        </h2>
        <button
          type="button"
          className="ot-icon-btn ot-close"
          onClick={onClose}
          aria-label={t("Close")}
          style={{ width: phone ? "44px" : "36px", height: phone ? "44px" : "36px", borderRadius: "999px", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flex: "none" }}
        >
          <Icon name="close" color="currentColor" />
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: phone ? "column" : "row", gap: "10px", padding: phone ? "0 16px 12px" : "0 24px 14px", flex: "none" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
          <span aria-hidden="true" style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", display: "flex" }}>
            <Icon name="search" size={18} />
          </span>
          <input
            type="search"
            className="ot-field"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Name, SKU or barcode")}
            aria-label={t("Name, SKU or barcode")}
            autoComplete="off"
            style={{ ...fieldStyle(phone), paddingLeft: "36px" }}
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", flex: "none" }}>
          <span style={{ fontSize: "13px", fontWeight: 500, color: TEXT_MUTED }}>{t("Group")}</span>
          <select
            className="ot-field"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            style={{ ...fieldStyle(phone), width: phone ? "100%" : "180px", padding: "0 8px" }}
          >
            <option value="">{t("All")}</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div
        className="scroll-container"
        aria-busy={loading || undefined}
        style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", borderTop: `1px solid ${BORDER_SOFT}` }}
      >
        {items.length > 0 && (phone ? phoneList : desktopList)}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: phone ? "12px 16px" : "12px 24px" }}>
          <p role="status" style={{ margin: 0, fontSize: "13px", color: TEXT_DIM }}>
            {status}
          </p>
          {items.length > 0 && items.length < total && (
            <PillButton large={phone} disabled={loading} onClick={() => load(page + 1, true)}>
              {loading ? t("Loading...") : t("Load more")}
            </PillButton>
          )}
          {failed && !items.length && (
            <PillButton large={phone} onClick={() => load(1, false)}>
              {t("Try again")}
            </PillButton>
          )}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: phone ? "wrap" : "nowrap",
          alignItems: "center",
          gap: "10px",
          padding: phone ? "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))" : "14px 24px",
          borderTop: `1px solid ${BORDER_SOFT}`,
          flex: "none",
        }}
      >
        <p aria-live="polite" style={{ margin: 0, flex: phone ? "1 1 100%" : 1, fontSize: "14px", color: TEXT_MUTED }}>
          {fill(t("{n} selected"), { n: count })}
        </p>
        <PillButton large={phone} onClick={onClose} style={phone ? { flex: 1 } : undefined}>
          {t("Cancel")}
        </PillButton>
        <PillButton variant="primary" large={phone} disabled={count === 0} onClick={add} style={phone ? { flex: 1 } : undefined}>
          {count === 1 ? t("Add 1 product") : fill(t("Add {n} products"), { n: count })}
        </PillButton>
      </div>
    </DialogFrame>
  );
};

export default AddProductsDialog;
